






















































































































































import { Component, Mixins, Watch } from 'vue-property-decorator'
import { Track } from '@/classes/track'
import Multiselect from 'vue-multiselect'
import VueSlider from 'vue-slider-component'
import { isSimilarKey, toCamelotKey } from '@/helpers/harmonic'
import { LibraryMixin } from '@/mixins'
import { USAGE_STAT_FEATURES } from '@/system/usage-stats'
import { spawn, Worker } from 'threads'
import { trimSpecialCharacters } from '@/helpers/features/smartfixes/remove-garbage'
import { ITutorialSlide } from '@/components/Tutorial.vue'
import * as _ from 'lodash'

const LOCALSTORAGE_OPTIONS_KEY = 'SimilarTrackOptions'

const LOCALSTORAGE_OPTIONS_PROPS = ['selectedGenres', 'bpmRange', 'matchKey', 'matchColor', 'matchRating', 'rating', 'cuePointsOnly', 'includeFalfOrDoubleBpm']

function trackSorter(a: Track, b: Track): number {
  if (!a.artist) return -1

  return a.artist.localeCompare(b.artist) // || a.albumTitle.localeCompare(b.albumTitle)
}

function blurAll(parentEl: HTMLElement) {
  const tmp = document.createElement('input')
  parentEl.appendChild(tmp)
  tmp.focus()
  tmp.remove()
}

@Component({
  components: {
    Multiselect,
    VueSlider,
    TrackResult: require('./TrackResult').default,
    PlaylistSaveModal: require('@/components/PlaylistSaveModal').default,
    Tutorial: require('@/components/Tutorial').default
  }
})
export default class SimilarTracks extends Mixins(LibraryMixin) {
  search: string = ''
  baseTrack: Track | null = null
  selectedGenres: string[] = []
  bpmRange: string | null = '6%'
  matchKey: boolean = true
  matchColor: boolean = false
  matchRating: boolean = false
  yearFrom: number | null = null
  yearTo: number | null = null
  rating: number[] = [0, 5]
  cuePointsOnly: boolean = false
  includeFalfOrDoubleBpm: boolean = false
  pinnedIndex: number = -1 // This index will be preserved in the results

  results: Track[] | null = null
  allResults: Track[] = []
  highlightedTrackIndex: number = -1
  ignoreKeys: boolean = false
  searchDebounce: any = null
  searching: boolean = false
  genres: string[] = []

  history: Set<string> = new Set<string>() // These aren't shown again in the results

  sentUsageStat: boolean = false

  tutorialSlides: ITutorialSlide[] = [
    {
      html: `By using <i>Similar Tracks</i> you can find your next track to mix very quickly. With a well organized library, it becomes even more powerful.<br>You can see Similar Tracks as a more powerful and advanced version of the traffic light system that Rekordbox has.<br><br>In this tutorial I'm going to walk through the options and show you how you can organize your library to get the most out of this.`
    },
    {
      image: 'similartracks1.png',
      html: `Start by choosing your <i>Base track</i>. This is the track I want to base my results on, usually the track I'm playing right now.<br>In this case I'm choosing a drum & bass track and I want to see some tracks to mix with this one.`
    },
    {
      image: 'similartracks2.png',
      html: `Rekordcloud shows me a list of tracks that are similar to the base track.<br>If I'm satisfied with a result, I can click it and quickly set it as the new base track. This way the page stays up-to-date with what I'm playing right now.<br><br>If I press <i>Search</i>, the list is updated with random results that match the filters.`
    },
    {
      image: 'similartracks3.png',
      html: `The options is what makes Similar Tracks so powerful. My library uses ratings to indicate energy level, so if I want to stay at the heavier drum & bass, I'd select rating 5 and I'll know that the results will only be heavy tracks.<br><br>I can also select genres to make sure I stay within certain genres and I change the BPM range of the results. This is why a well prepared library can be really helpful!<br><br>And finally, there is also an option to save the results to a playlist so I can browse the results in my DJ app.<br><br>That's the end of the tutorial. With a well organized library, Similar Tracks can be extremely powerful and you can be sure to find the right track to mix.`
    }
  ]

  // keyboard control
  // F = open base track search
  // G = open genre
  // space = search
  // B = open bpm range
  // 012345 + shift, numpad = rating
  // K = key
  // C = color
  // R = rating
  // P = cue points
  // I = include half double
  // up/down = navigate results
  // enter = select new base track from results
  // shift+delete = reset

  // Note: Possible features for later:
  // auto select genre of base track when no genre has been selected?
  // show duration, genre?
  // disable BPM range if basetrack has no bpm? same with other filters?

  get tracks(): Track[] {
    if (!this.library) return []

    return this.library.tracks
  }

  get filteredTracks(): Track[] {
    if (!this.search) return []

    const s = this.search.toLowerCase()

    return this.tracks
      .filter(
        t =>
          (t.title && t.title.toLowerCase().includes(s)) ||
          (t.artist && t.artist.toLowerCase().includes(s)) ||
          (t.albumTitle && t.albumTitle.toLowerCase().includes(s))
      )
      .sort(trackSorter)
  }

  get valid(): boolean {
    return !!this.baseTrack
  }

  get suggestedPlaylistName(): string {
    if (this.baseTrack) {
      let s: string = ''

      if (this.baseTrack.artist) s += this.baseTrack.artist

      if (this.baseTrack.title) {
        if (s) s += ' - '

        s += this.baseTrack.title
      }

      s = trimSpecialCharacters(s)

      if (s.length > 30) {
        s = s.slice(0, 30 - 3) + '...'
      }

      return 'Similar Tracks: ' + s
    }

    return ''
  }

  get playlistSaveDisabled(): boolean {
    return !this.baseTrack || this.allResults.length === 0
  }

  saveToLocalStorage(): void {
    const options: any = {}

    for (const prop of LOCALSTORAGE_OPTIONS_PROPS) {
      options[prop] = (this as any)[prop]
    }

    localStorage.setItem(LOCALSTORAGE_OPTIONS_KEY, JSON.stringify(options))
  }

  loadFromLocalStorage(): void {
    const options: any = JSON.parse(localStorage.getItem(LOCALSTORAGE_OPTIONS_KEY) || '{}')

    for (const prop of LOCALSTORAGE_OPTIONS_PROPS) {
      const v = options[prop]

      if (v !== undefined) {
        ; (this as any)[prop] = v
      }
    }
  }

  blur() {
    blurAll((this.$refs as any).options.$el)
  }

  reset(): void {
    this.matchKey = true
    this.matchRating = false
    this.matchColor = false
    this.cuePointsOnly = false
    this.includeFalfOrDoubleBpm = false
    this.rating = [0, 5]
    this.bpmRange = '6%'
    this.selectedGenres = []
    this.yearFrom = null
    this.yearTo = null

    this.saveToLocalStorage()
  }

  setSelected(track: Track): void {
    this.baseTrack = track
  }

  moreLikeThis(track: Track, index: number): void {
    this.pinnedIndex = index

    this.searchTracks(track)
  }

  onSearchChange(s: string): void {
    clearTimeout(this.searchDebounce)
    this.searching = true

    this.searchDebounce = setTimeout(() => {
      this.search = s
      this.searching = false
    }, 300)
  }

  createTrackSearchLabel(track: Track): string {
    return `${track.artist} - ${track.title} - ${track.albumTitle}`
  }

  searchTracks(overrideTrack?: Track): void {
    if (!this.baseTrack && !overrideTrack) return

    const baseTrack: Track = overrideTrack || (this.baseTrack as Track)

    let tracks: Track[] = this.tracks

    // Filter history
    tracks = tracks.filter(t => !this.history.has(t._id))

    // Year
    if (this.yearFrom !== null) {
      tracks = tracks.filter(t => t.year && t.year >= (this.yearFrom as number))
    }

    if (this.yearTo !== null) {
      tracks = tracks.filter(t => t.year && t.year <= (this.yearTo as number))
    }

    // Genres
    if (this.selectedGenres && this.selectedGenres.length > 0) {
      tracks = tracks.filter(t => this.selectedGenres.includes(t.genre))
    }

    // BPM
    if (this.bpmRange) {
      const isPercentage: boolean = this.bpmRange.includes('%')
      const n: number = parseInt(this.bpmRange.replace('%', ''), 10)
      const baseBpm: number | null = baseTrack.bpm

      if (baseBpm) {
        let startBpmRange: number
        let endBpmRange: number

        if (isPercentage) {
          startBpmRange = (baseBpm * (100 - n)) / 100
          endBpmRange = (baseBpm * (100 + n)) / 100
        } else {
          startBpmRange = baseBpm - n
          endBpmRange = baseBpm + n
        }

        tracks = tracks.filter(t => {
          if (t.bpm === null) return false

          if (this.includeFalfOrDoubleBpm) {
            if (t.bpm >= startBpmRange / 2 && t.bpm <= endBpmRange / 2) return true
            if (t.bpm >= startBpmRange * 2 && t.bpm <= endBpmRange * 2) return true
          }

          return t.bpm >= startBpmRange && t.bpm <= endBpmRange
        })
      }
    }

    // Rating (based on 255 / 5)
    if (this.matchRating) {
      // Rating can be undefined for some unknown reason
      tracks = tracks.filter(t => t.rating === baseTrack.rating)
    } else {
      const startRatingRange: number = Math.min(this.rating[0], this.rating[1]) * 51
      const endRatingRange: number = Math.max(this.rating[0], this.rating[1]) * 51

      tracks = tracks.filter(t => t.rating >= startRatingRange && t.rating <= endRatingRange)
    }

    // Color
    if (this.matchColor) {
      tracks = tracks.filter(t => t.color === baseTrack.color)
    }

    // Harmonic key
    if (this.matchKey && baseTrack.key) {
      const baseKey: string = toCamelotKey(baseTrack.key)

      if (baseKey) {
        tracks = tracks.filter(t => {
          if (!t.key) return false

          return isSimilarKey(t.key, baseKey)
        })
      }
    }

    // Must have cue points
    if (this.cuePointsOnly) {
      tracks = tracks.filter(t => t.cuePointsAmount > 0)
    }

    // Filter selected track
    tracks = tracks.filter(t => t._id !== baseTrack._id)

    this.allResults = _.shuffle(tracks)

    const results = this.allResults
      .slice(0, 10)
      .sort(trackSorter)

    if (this.results && this.pinnedIndex > -1) {
      const pinnedTrack: Track = this.results[this.pinnedIndex]

      results.splice(this.pinnedIndex, 1, pinnedTrack)

      this.pinnedIndex = -1
    }

    this.results = results

    if (!this.sentUsageStat) {
      this.sentUsageStat = true

      window.addUsageStat(USAGE_STAT_FEATURES.SimilarTracks, 'search')
    }
  }

  setIgnoreKeys(v: boolean) {
    this.ignoreKeys = v
  }

  processKeyEvent(e: KeyboardEvent): void {
    if (e.code === 'Escape') {
      const bpmRangeSelect = (this.$refs as any).bpmRangeSelect

      if (bpmRangeSelect) {
        bpmRangeSelect.$el.querySelector('select').blur()
      }

      this.setIgnoreKeys(false)

      return
    }

    if (this.ignoreKeys || e.ctrlKey || e.altKey || e.metaKey) return // Skip everything with CTRL, ALT or Meta because they are browser or OS commands

    if (e.code === 'KeyF') {
      // @open is not triggered when doing the below
      this.setIgnoreKeys(true)

      e.preventDefault()

      const baseTrackSelect = (this.$refs as any).baseTrackSelect

      if (baseTrackSelect) {
        baseTrackSelect.isOpen = true
        baseTrackSelect.$el.querySelector('.multiselect__input').focus()
      }
    } else if (e.code === 'KeyG') {
      this.setIgnoreKeys(true)

      e.preventDefault()

      const genreSelect = (this.$refs as any).genreSelect

      if (genreSelect) {
        genreSelect.isOpen = true
        genreSelect.$el.querySelector('.multiselect__input').focus()
      }
    } else if (e.code === 'Space') {
      e.preventDefault()

      this.searchTracks()
    } else if (e.code === 'KeyB') {
      this.setIgnoreKeys(true)

      const bpmRangeSelect = (this.$refs as any).bpmRangeSelect

      if (bpmRangeSelect) {
        bpmRangeSelect.focus()
      }
    } else if (
      e.code === 'Digit0' ||
      e.code === 'Digit1' ||
      e.code === 'Digit2' ||
      e.code === 'Digit3' ||
      e.code === 'Digit4' ||
      e.code === 'Digit5' ||
      e.code === 'Numpad0' ||
      e.code === 'Numpad1' ||
      e.code === 'Numpad2' ||
      e.code === 'Numpad3' ||
      e.code === 'Numpad4' ||
      e.code === 'Numpad5'
    ) {
      let s: string = e.code

      if (s.includes('Digit')) s = s.replace('Digit', '')
      if (s.includes('Numpad')) s = s.replace('Numpad', '')

      const r = parseInt(s, 10)

      if (e.shiftKey) {
        this.$set(this.rating, 1, r)
      } else {
        this.$set(this.rating, 0, r)
      }
    } else if (e.code === 'KeyK') {
      this.matchKey = !this.matchKey
    } else if (e.code === 'KeyC') {
      this.matchColor = !this.matchColor
    } else if (e.code === 'KeyR') {
      this.matchRating = !this.matchRating
    } else if (e.code === 'KeyP') {
      this.cuePointsOnly = !this.cuePointsOnly
    } else if (e.code === 'KeyI') {
      this.includeFalfOrDoubleBpm = !this.includeFalfOrDoubleBpm
    } else if (e.code === 'ArrowDown') {
      if (!this.results) return

      if (this.highlightedTrackIndex === this.results.length - 1) {
        this.highlightedTrackIndex = 0
      } else {
        this.highlightedTrackIndex += 1
      }
    } else if (e.code === 'ArrowUp') {
      if (!this.results) return

      if (this.highlightedTrackIndex === -1 || this.highlightedTrackIndex === 0) {
        this.highlightedTrackIndex = this.results.length - 1
      } else {
        this.highlightedTrackIndex -= 1
      }
    } else if (e.code === 'Enter') {
      if (!this.results || this.highlightedTrackIndex === -1) return

      this.setSelected(this.results[this.highlightedTrackIndex])
      this.searchTracks()
    } else if (e.code === 'Delete' && e.shiftKey) {
      this.reset()
    }
  }

  async created() {
    this.loadFromLocalStorage()

    const worker = await spawn(new Worker('@/helpers/features/genre-renamer'))

    this.genres = await worker.findAllUniqueGenres(this.tracks)

    for (const prop of LOCALSTORAGE_OPTIONS_PROPS) {
      this.$watch(prop, this.saveToLocalStorage)
    }

    window.addEventListener('keydown', this.processKeyEvent)
  }

  destroyed() {
    window.removeEventListener('keydown', this.processKeyEvent)
  }

  @Watch('baseTrack')
  watchSelectTrack() {
    this.highlightedTrackIndex = -1

    if (this.baseTrack) {
      this.history.add(this.baseTrack._id)
    }
  }
}
